import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { marketFor } from '@/lib/market';
import { themed } from '@/lib/theme';
import { APP_NAME, DEFAULT_PARTNER_SLUG, usePartnerStore, type PartnerConfig } from '@/stores/partner.store';

/**
 * Public marketing landing page — "Daybreak Signal" design.
 *
 * The page ships with its own self-contained stylesheet (below). Its design
 * tokens are scoped to `.pulse-landing` (NOT :root) so they never clash with
 * the app-wide RGB-channel tokens defined in index.css. The small vanilla-JS
 * interactions from the reference are ported into a scoped effect.
 */
const CSS = `
.pulse-landing{
  --paper:#F5F7FC; --white:#FFFFFF; --peach:#FFEFE6; --peach-deep:#FFE0D0;
  --ink:#0F1A33; --ink-2:#55617F; --ink-3:#8A93AC;
  --blue:#0E7CFF; --blue-ink:#0B2A6B; --sky:#8AD4FF;
  --coral:#FF6A3D; --lime:#C9F24B;
  --line:rgba(15,26,51,.10); --line-2:rgba(15,26,51,.06);
  --shadow-s:0 1px 2px rgba(15,26,51,.05), 0 4px 14px rgba(15,26,51,.05);
  --shadow-m:0 2px 6px rgba(15,26,51,.05), 0 18px 40px -12px rgba(15,26,51,.14);
  --shadow-l:0 30px 70px -20px rgba(11,42,107,.28), 0 6px 18px rgba(15,26,51,.06);
  --r-s:12px; --r-m:18px; --r-l:26px; --r-xl:34px;
  --display:'Bricolage Grotesque','Instrument Sans',system-ui,sans-serif;
  --body:'Instrument Sans',system-ui,-apple-system,sans-serif;
  --mono:'DM Mono',ui-monospace,'SFMono-Regular',monospace;
  --maxw:1200px; --pad:clamp(18px,4vw,40px);
  background:var(--paper); color:var(--ink);
  font-family:var(--body); font-size:17px; line-height:1.6;
  -webkit-font-smoothing:antialiased; overflow-x:hidden;
}
.pulse-landing *,.pulse-landing *::before,.pulse-landing *::after{box-sizing:border-box;}
.pulse-landing img{max-width:100%;display:block;}
.pulse-landing a{color:inherit;text-decoration:none;}
.pulse-landing button{font:inherit;color:inherit;cursor:pointer;border:0;background:none;}
.pulse-landing :focus-visible{outline:3px solid var(--coral);outline-offset:3px;border-radius:6px;}
.pulse-landing h1,.pulse-landing h2,.pulse-landing h3,.pulse-landing h4{font-family:var(--display);margin:0;line-height:1.04;letter-spacing:-.03em;font-weight:700;}
.pulse-landing h1{font-size:clamp(2.6rem,6.2vw,4.6rem);}
.pulse-landing h2{font-size:clamp(2.05rem,4.4vw,3.35rem);}
.pulse-landing h3{font-size:clamp(1.25rem,2vw,1.6rem);letter-spacing:-.02em;line-height:1.15;}
.pulse-landing p{margin:0;}
.pulse-landing .wrap{max-width:var(--maxw);margin:0 auto;padding-inline:var(--pad);}
.pulse-landing .eyebrow{font-family:var(--mono);font-size:.72rem;text-transform:uppercase;letter-spacing:.16em;color:var(--ink-3);}
.pulse-landing .lede{font-size:clamp(1.02rem,1.5vw,1.18rem);color:var(--ink-2);line-height:1.62;}
.pulse-landing .section{padding-block:clamp(72px,9vw,124px);position:relative;}
.pulse-landing .btn{display:inline-flex;align-items:center;justify-content:center;gap:.55em;padding:.86em 1.5em;border-radius:999px;font-weight:600;font-size:.97rem;letter-spacing:-.01em;transition:transform .18s ease, box-shadow .18s ease, background .18s ease;white-space:nowrap;}
.pulse-landing .btn:hover{transform:translateY(-2px);}
.pulse-landing .btn-primary{background:var(--ink);color:#fff;box-shadow:0 8px 22px -8px rgba(15,26,51,.6);}
.pulse-landing .btn-primary:hover{background:var(--blue-ink);}
.pulse-landing .btn-accent{background:var(--coral);color:#fff;box-shadow:0 10px 24px -8px rgba(255,106,61,.65);}
.pulse-landing .btn-ghost{background:rgba(255,255,255,.7);border:1px solid var(--line);color:var(--ink);}
.pulse-landing .btn-ghost:hover{background:#fff;border-color:rgba(15,26,51,.2);}
.pulse-landing .btn-lg{padding:1.02em 1.85em;font-size:1.03rem;}
.pulse-landing .topbar{background:var(--ink);color:#fff;font-size:.86rem;display:flex;justify-content:center;align-items:center;gap:.7em;padding:.68em var(--pad);text-align:center;flex-wrap:wrap;}
.pulse-landing .topbar .dot{width:7px;height:7px;border-radius:50%;background:var(--lime);flex:none;animation:pl-ping 2.4s ease-out infinite;}
@keyframes pl-ping{0%{box-shadow:0 0 0 0 rgba(201,242,75,.6)}70%{box-shadow:0 0 0 9px rgba(201,242,75,0)}100%{box-shadow:0 0 0 0 rgba(201,242,75,0)}}
.pulse-landing .topbar a{color:var(--sky);font-weight:600;text-decoration:underline;text-underline-offset:3px;}
.pulse-landing .nav-shell{position:sticky;top:0;z-index:80;}
.pulse-landing .nav{background:rgba(245,247,252,.82);backdrop-filter:blur(14px);border-bottom:1px solid transparent;transition:border-color .25s, background .25s;}
.pulse-landing .nav.stuck{border-bottom-color:var(--line);background:rgba(245,247,252,.94);}
.pulse-landing .nav-in{display:flex;align-items:center;gap:1.6rem;height:70px;}
.pulse-landing .logo{display:flex;align-items:center;gap:.55rem;font-family:var(--display);font-weight:700;font-size:1.32rem;letter-spacing:-.04em;flex:none;}
.pulse-landing .logo-mark{width:32px;height:32px;border-radius:10px;flex:none;background:linear-gradient(145deg,var(--blue),var(--blue-ink));display:grid;place-items:center;box-shadow:0 6px 14px -5px rgba(14,124,255,.7);}
.pulse-landing .logo-mark svg{width:19px;height:19px;}
.pulse-landing .logo-dot{color:var(--coral);margin-left:-.5rem;}
.pulse-landing .logo small{font-family:var(--mono);font-size:.6rem;letter-spacing:.14em;color:var(--ink-3);text-transform:uppercase;align-self:flex-end;padding-bottom:.42em;}
.pulse-landing .nav-links{display:flex;align-items:center;gap:.25rem;margin-left:.5rem;}
.pulse-landing .nav-links a{padding:.5em .82em;border-radius:999px;font-size:.94rem;font-weight:500;color:var(--ink-2);display:inline-flex;align-items:center;gap:.35em;transition:background .18s,color .18s;}
.pulse-landing .nav-links a:hover{background:rgba(15,26,51,.06);color:var(--ink);}
.pulse-landing .nav-actions{margin-left:auto;display:flex;align-items:center;gap:.6rem;}
.pulse-landing .nav-actions .signin{font-size:.94rem;font-weight:500;color:var(--ink-2);padding:.5em .7em;}
.pulse-landing .nav-actions .signin:hover{color:var(--ink);}
.pulse-landing .burger{display:none;width:42px;height:42px;border-radius:12px;border:1px solid var(--line);place-items:center;background:#fff;}
.pulse-landing .burger span{display:block;width:17px;height:2px;background:var(--ink);border-radius:2px;position:relative;}
.pulse-landing .burger span::before,.pulse-landing .burger span::after{content:'';position:absolute;left:0;width:17px;height:2px;background:var(--ink);border-radius:2px;}
.pulse-landing .burger span::before{top:-5px;}.pulse-landing .burger span::after{top:5px;}
.pulse-landing .mobile-menu{display:none;background:#fff;border-bottom:1px solid var(--line);padding:1rem var(--pad) 1.4rem;}
.pulse-landing .mobile-menu.open{display:block;}
.pulse-landing .mobile-menu a{display:block;padding:.72em 0;font-weight:500;border-bottom:1px solid var(--line-2);}
.pulse-landing .mobile-menu .btn{width:100%;margin-top:1rem;}
.pulse-landing .hero{position:relative;padding-top:clamp(38px,5vw,64px);padding-bottom:clamp(60px,7vw,96px);overflow:hidden;}
.pulse-landing .hero-glow{position:absolute;inset:-30% -10% auto -10%;height:820px;z-index:0;pointer-events:none;background:radial-gradient(46% 46% at 18% 30%, rgba(255,106,61,.16), transparent 70%),radial-gradient(50% 50% at 78% 18%, rgba(14,124,255,.20), transparent 70%),radial-gradient(40% 40% at 52% 62%, rgba(138,212,255,.18), transparent 70%);filter:blur(10px);}
.pulse-landing .hero-in{position:relative;z-index:1;display:grid;grid-template-columns:1.02fr .98fr;gap:clamp(30px,4vw,62px);align-items:center;}
.pulse-landing .rating{display:inline-flex;align-items:center;gap:.6rem;background:#fff;border:1px solid var(--line);border-radius:999px;padding:.5em 1em;box-shadow:var(--shadow-s);font-size:.84rem;color:var(--ink-2);}
.pulse-landing .rating b{color:var(--ink);font-weight:600;}
.pulse-landing .rating .stars{color:var(--coral);letter-spacing:-1px;}
.pulse-landing .hero h1{margin:1.15rem 0 0;}
.pulse-landing .hero h1 .accent{background:linear-gradient(100deg,var(--blue) 10%,var(--coral) 90%);-webkit-background-clip:text;background-clip:text;color:transparent;}
.pulse-landing .hero .lede{margin-top:1.15rem;max-width:33em;}
.pulse-landing .hero-cta{display:flex;gap:.75rem;margin-top:1.9rem;flex-wrap:wrap;}
.pulse-landing .hero-trust{display:flex;gap:1.35rem;margin-top:1.5rem;flex-wrap:wrap;font-family:var(--mono);font-size:.76rem;color:var(--ink-2);letter-spacing:.02em;}
.pulse-landing .hero-trust span{display:inline-flex;align-items:center;gap:.45em;}
.pulse-landing .hero-trust svg{width:14px;height:14px;color:var(--blue);flex:none;}
.pulse-landing .console{background:#fff;border-radius:var(--r-xl);box-shadow:var(--shadow-l);border:1px solid rgba(255,255,255,.9);overflow:hidden;position:relative;}
.pulse-landing .console-bar{display:flex;align-items:center;gap:.6rem;padding:.82rem 1.1rem;border-bottom:1px solid var(--line-2);background:linear-gradient(180deg,#FBFCFE,#F6F8FC);}
.pulse-landing .console-bar .tl{display:flex;gap:6px;}
.pulse-landing .console-bar .tl i{width:10px;height:10px;border-radius:50%;display:block;background:#E3E7F0;}
.pulse-landing .console-bar .tl i:nth-child(1){background:#FFC9B7;}
.pulse-landing .console-bar .tl i:nth-child(2){background:#FFE6A8;}
.pulse-landing .console-bar .tl i:nth-child(3){background:#C7E8B4;}
.pulse-landing .console-bar .title{font-family:var(--mono);font-size:.74rem;color:var(--ink-3);letter-spacing:.06em;}
.pulse-landing .live-pill{margin-left:auto;display:inline-flex;align-items:center;gap:.4em;background:rgba(201,242,75,.22);color:#4E6B00;border:1px solid rgba(155,199,43,.4);font-family:var(--mono);font-size:.66rem;letter-spacing:.14em;text-transform:uppercase;padding:.3em .7em;border-radius:999px;}
.pulse-landing .live-pill i{width:6px;height:6px;border-radius:50%;background:#7FA80F;animation:pl-ping2 1.8s infinite;}
@keyframes pl-ping2{0%,100%{opacity:1}50%{opacity:.25}}
.pulse-landing .console-metrics{display:grid;grid-template-columns:repeat(3,1fr);border-bottom:1px solid var(--line-2);}
.pulse-landing .cm{padding:.95rem 1.1rem;border-right:1px solid var(--line-2);}
.pulse-landing .cm:last-child{border-right:0;}
.pulse-landing .cm .k{font-family:var(--mono);font-size:.63rem;letter-spacing:.13em;text-transform:uppercase;color:var(--ink-3);}
.pulse-landing .cm .v{font-family:var(--display);font-weight:700;font-size:1.42rem;letter-spacing:-.03em;margin-top:.15rem;}
.pulse-landing .cm .v small{font-size:.72rem;font-weight:500;color:var(--ink-3);font-family:var(--mono);letter-spacing:0;}
.pulse-landing .cm .v .up{color:#3E8E24;font-size:.68rem;font-family:var(--mono);}
.pulse-landing .queue{list-style:none;margin:0;padding:.55rem;min-height:296px;}
.pulse-landing .qi{display:flex;align-items:center;gap:.8rem;padding:.72rem .8rem;border-radius:16px;transition:background .25s;position:relative;}
.pulse-landing .qi + .qi{margin-top:.1rem;}
.pulse-landing .qi.enter{animation:pl-slideIn .45s cubic-bezier(.2,.8,.25,1) both;}
.pulse-landing .qi.leave{animation:pl-slideOut .5s cubic-bezier(.4,0,.7,.2) both;}
@keyframes pl-slideIn{from{opacity:0;transform:translateY(14px) scale(.97);}to{opacity:1;transform:none;}}
@keyframes pl-slideOut{to{opacity:0;transform:translateX(26px) scale(.97);max-height:0;padding-block:0;margin:0;}}
.pulse-landing .qi .av{width:34px;height:34px;border-radius:11px;flex:none;display:grid;place-items:center;font-family:var(--display);font-weight:700;font-size:.82rem;color:#fff;letter-spacing:-.02em;}
.pulse-landing .qi .meta{min-width:0;flex:1;}
.pulse-landing .qi .who{font-weight:600;font-size:.9rem;display:flex;align-items:center;gap:.45rem;}
.pulse-landing .qi .who .tag{font-family:var(--mono);font-size:.58rem;letter-spacing:.1em;text-transform:uppercase;padding:.16em .5em;border-radius:5px;background:rgba(14,124,255,.1);color:#0B5FCC;font-weight:500;}
.pulse-landing .qi .who .tag.bill{background:rgba(255,106,61,.13);color:#C3421A;}
.pulse-landing .qi .who .tag.tech{background:rgba(138,110,255,.13);color:#5B3FD1;}
.pulse-landing .qi .msg{font-size:.83rem;color:var(--ink-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:.1rem;}
.pulse-landing .qi .state{font-family:var(--mono);font-size:.66rem;letter-spacing:.04em;flex:none;text-align:right;color:var(--ink-3);min-width:104px;}
.pulse-landing .qi .state.drafting{color:var(--blue);}
.pulse-landing .qi .state.resolved{color:#3E8E24;}
.pulse-landing .qi .state .bar{display:block;height:3px;border-radius:2px;background:rgba(14,124,255,.16);margin-top:.35rem;overflow:hidden;}
.pulse-landing .qi .state .bar i{display:block;height:100%;width:0;background:var(--blue);border-radius:2px;animation:pl-fill 1.15s linear forwards;}
@keyframes pl-fill{to{width:100%;}}
.pulse-landing .qi.is-resolved{background:rgba(201,242,75,.14);}
.pulse-landing .console-foot{border-top:1px solid var(--line-2);padding:.6rem 1.1rem .75rem;display:flex;align-items:center;gap:1rem;background:#FBFCFE;}
.pulse-landing .ecg{flex:1;height:38px;overflow:hidden;}
.pulse-landing .ecg svg{width:100%;height:100%;}
.pulse-landing .ecg path{fill:none;stroke:var(--blue);stroke-width:2;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:1400;stroke-dashoffset:1400;animation:pl-trace 4.5s linear infinite;}
@keyframes pl-trace{to{stroke-dashoffset:-1400;}}
.pulse-landing .console-foot .tot{font-family:var(--mono);font-size:.7rem;color:var(--ink-2);white-space:nowrap;}
.pulse-landing .console-foot .tot b{color:var(--ink);font-size:.86rem;}
.pulse-landing .proof{padding-block:clamp(30px,4vw,44px);border-block:1px solid var(--line-2);background:rgba(255,255,255,.55);}
.pulse-landing .proof .cap{text-align:center;margin-bottom:1.4rem;}
.pulse-landing .marquee{overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent);mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent);}
.pulse-landing .marquee-track{display:flex;gap:3.4rem;width:max-content;animation:pl-slide 34s linear infinite;}
@keyframes pl-slide{to{transform:translateX(-50%);}}
.pulse-landing .marquee:hover .marquee-track{animation-play-state:paused;}
.pulse-landing .marquee span{font-family:var(--display);font-weight:600;font-size:1.28rem;letter-spacing:-.035em;color:var(--ink);opacity:.42;white-space:nowrap;transition:opacity .2s;}
.pulse-landing .split-head{text-align:center;max-width:46rem;margin:0 auto clamp(34px,4vw,54px);}
.pulse-landing .split-head .lede{margin-top:1rem;}
.pulse-landing .swap .old{color:var(--ink-3);text-decoration:line-through;text-decoration-color:rgba(255,106,61,.65);text-decoration-thickness:4px;}
.pulse-landing .swap .new{color:var(--blue);}
.pulse-landing .cols{display:grid;grid-template-columns:1fr 1fr;gap:clamp(16px,2vw,24px);}
.pulse-landing .col{border-radius:var(--r-l);padding:clamp(22px,2.6vw,34px);}
.pulse-landing .col-a{background:rgba(255,255,255,.6);border:1px dashed rgba(15,26,51,.18);}
.pulse-landing .col-b{background:linear-gradient(165deg,#0E7CFF,#0B2A6B);color:#fff;box-shadow:var(--shadow-l);position:relative;overflow:hidden;}
.pulse-landing .col-b::after{content:'';position:absolute;inset:auto -20% -40% -20%;height:260px;background:radial-gradient(50% 60% at 50% 100%, rgba(201,242,75,.28), transparent 70%);pointer-events:none;}
.pulse-landing .col-title{display:flex;align-items:center;gap:.6rem;font-family:var(--display);font-weight:700;font-size:1.1rem;letter-spacing:-.02em;}
.pulse-landing .col-a .col-title{color:var(--ink-2);}
.pulse-landing .col .row{padding:1.05rem 0;border-top:1px solid var(--line);}
.pulse-landing .col-b .row{border-top-color:rgba(255,255,255,.16);}
.pulse-landing .col .row:first-of-type{margin-top:1.3rem;}
.pulse-landing .col .row h4{font-family:var(--display);font-size:1.03rem;font-weight:600;letter-spacing:-.02em;display:flex;gap:.55rem;align-items:flex-start;}
.pulse-landing .col .row p{font-size:.92rem;margin-top:.32rem;padding-left:1.65rem;}
.pulse-landing .col-a .row p{color:var(--ink-2);}
.pulse-landing .col-b .row p{color:rgba(255,255,255,.78);}
.pulse-landing .ic{width:20px;height:20px;flex:none;border-radius:7px;display:grid;place-items:center;margin-top:.05rem;}
.pulse-landing .col-a .ic{background:rgba(15,26,51,.07);color:var(--ink-3);}
.pulse-landing .col-b .ic{background:rgba(201,242,75,.22);color:var(--lime);}
.pulse-landing .ic svg{width:12px;height:12px;}
.pulse-landing .col-b .kicker{position:relative;z-index:1;margin-top:1.6rem;font-family:var(--mono);font-size:.72rem;letter-spacing:.1em;text-transform:uppercase;color:var(--lime);}
.pulse-landing .bento{display:grid;grid-template-columns:repeat(6,1fr);gap:clamp(14px,1.6vw,20px);}
.pulse-landing .card{background:#fff;border-radius:var(--r-l);border:1px solid var(--line-2);box-shadow:var(--shadow-s);padding:clamp(22px,2.4vw,30px);position:relative;overflow:hidden;transition:transform .25s ease, box-shadow .25s ease;}
.pulse-landing .card:hover{transform:translateY(-4px);box-shadow:var(--shadow-m);}
.pulse-landing .card h3{margin-top:.85rem;}
.pulse-landing .card p{color:var(--ink-2);font-size:.95rem;margin-top:.55rem;}
.pulse-landing .card .chip{font-family:var(--mono);font-size:.63rem;letter-spacing:.12em;text-transform:uppercase;color:var(--blue);}
.pulse-landing .b-1{grid-column:span 3;}.pulse-landing .b-2{grid-column:span 3;}
.pulse-landing .b-3{grid-column:span 2;}.pulse-landing .b-4{grid-column:span 2;}.pulse-landing .b-5{grid-column:span 2;}
.pulse-landing .card-peach{background:linear-gradient(160deg,var(--peach),#FFF7F2);border-color:rgba(255,106,61,.16);}
.pulse-landing .card-peach .chip{color:var(--coral);}
.pulse-landing .mini{margin-top:1.3rem;border-radius:14px;background:#F7F9FD;border:1px solid var(--line-2);padding:.85rem;}
.pulse-landing .mini-row{display:flex;align-items:center;gap:.55rem;font-size:.78rem;color:var(--ink-2);}
.pulse-landing .mini-row + .mini-row{margin-top:.5rem;}
.pulse-landing .mini-row .pill{font-family:var(--mono);font-size:.6rem;letter-spacing:.08em;padding:.2em .5em;border-radius:6px;background:#fff;border:1px solid var(--line);}
.pulse-landing .bars{display:flex;align-items:flex-end;gap:6px;height:64px;margin-top:1.3rem;}
.pulse-landing .bars i{flex:1;border-radius:5px 5px 3px 3px;background:rgba(14,124,255,.18);display:block;}
.pulse-landing .bars i.hot{background:linear-gradient(180deg,var(--coral),#FF9A6E);}
.pulse-landing .langs{display:flex;flex-wrap:wrap;gap:.4rem;margin-top:1.2rem;}
.pulse-landing .langs b{font-family:var(--mono);font-weight:400;font-size:.68rem;padding:.32em .6em;border-radius:8px;background:#fff;border:1px solid var(--line);color:var(--ink-2);}
.pulse-landing .feat{display:grid;grid-template-columns:1fr 1.08fr;gap:clamp(28px,4.5vw,70px);align-items:center;}
.pulse-landing .feat + .feat{margin-top:clamp(58px,7vw,104px);}
.pulse-landing .feat.rev .feat-copy{order:2;}
.pulse-landing .feat-copy h3{font-size:clamp(1.7rem,3vw,2.35rem);margin-top:.85rem;letter-spacing:-.03em;}
.pulse-landing .feat-copy p{margin-top:1rem;color:var(--ink-2);}
.pulse-landing .chips{display:flex;flex-wrap:wrap;gap:.5rem;margin-top:1.5rem;}
.pulse-landing .chips b{font-family:var(--mono);font-weight:400;font-size:.7rem;letter-spacing:.03em;background:#fff;border:1px solid var(--line);padding:.44em .82em;border-radius:999px;color:var(--ink-2);}
.pulse-landing .link-more{display:inline-flex;align-items:center;gap:.4em;margin-top:1.5rem;font-weight:600;font-size:.95rem;color:var(--blue);}
.pulse-landing .link-more:hover{gap:.75em;}
.pulse-landing .link-more svg{width:15px;height:15px;}
.pulse-landing .panel{background:#fff;border-radius:var(--r-l);box-shadow:var(--shadow-m);border:1px solid var(--line-2);overflow:hidden;}
.pulse-landing .panel-head{display:flex;align-items:center;gap:.55rem;padding:.75rem 1rem;border-bottom:1px solid var(--line-2);font-family:var(--mono);font-size:.68rem;letter-spacing:.1em;text-transform:uppercase;color:var(--ink-3);background:linear-gradient(180deg,#FBFCFE,#F7F9FD);}
.pulse-landing .panel-head .g{margin-left:auto;color:var(--blue);}
.pulse-landing .panel-body{padding:1.05rem;}
.pulse-landing .tri{display:flex;gap:.7rem;align-items:center;padding:.7rem;border-radius:13px;border:1px solid var(--line-2);}
.pulse-landing .tri + .tri{margin-top:.55rem;}
.pulse-landing .tri.hot{border-color:rgba(255,106,61,.35);background:rgba(255,239,230,.6);}
.pulse-landing .tri .lv{font-family:var(--mono);font-size:.6rem;letter-spacing:.1em;padding:.22em .5em;border-radius:6px;background:rgba(15,26,51,.06);color:var(--ink-2);flex:none;}
.pulse-landing .tri.hot .lv{background:var(--coral);color:#fff;}
.pulse-landing .tri .t{font-size:.87rem;font-weight:500;flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.pulse-landing .tri .r{font-family:var(--mono);font-size:.66rem;color:var(--ink-3);flex:none;}
.pulse-landing .composer{border:1px solid var(--line);border-radius:15px;padding:.9rem;}
.pulse-landing .composer .to{font-family:var(--mono);font-size:.66rem;color:var(--ink-3);letter-spacing:.06em;}
.pulse-landing .composer .txt{font-size:.9rem;line-height:1.55;margin-top:.6rem;}
.pulse-landing .composer .txt em{font-style:normal;background:linear-gradient(180deg,transparent 62%,rgba(201,242,75,.55) 0);}
.pulse-landing .composer .cursor{display:inline-block;width:2px;height:1em;background:var(--blue);vertical-align:-2px;animation:pl-blink 1s steps(2) infinite;}
@keyframes pl-blink{50%{opacity:0}}
.pulse-landing .tone{display:flex;gap:.4rem;margin-top:.9rem;flex-wrap:wrap;}
.pulse-landing .tone b{font-family:var(--mono);font-weight:400;font-size:.63rem;padding:.3em .62em;border-radius:7px;border:1px solid var(--line);color:var(--ink-2);}
.pulse-landing .tone b.on{background:var(--ink);color:#fff;border-color:var(--ink);}
.pulse-landing .spark{display:flex;align-items:flex-end;gap:5px;height:110px;}
.pulse-landing .spark i{flex:1;border-radius:4px 4px 2px 2px;background:linear-gradient(180deg,var(--sky),rgba(14,124,255,.25));display:block;}
.pulse-landing .spark i.pk{background:linear-gradient(180deg,var(--coral),#FFA982);}
.pulse-landing .legend{display:flex;gap:1rem;margin-top:.9rem;font-family:var(--mono);font-size:.66rem;color:var(--ink-3);}
.pulse-landing .legend span{display:inline-flex;align-items:center;gap:.4em;}
.pulse-landing .legend i{width:9px;height:9px;border-radius:3px;display:block;}
.pulse-landing .tl-item{display:flex;gap:.8rem;padding-bottom:1rem;position:relative;}
.pulse-landing .tl-item:not(:last-child)::before{content:'';position:absolute;left:11px;top:26px;bottom:0;width:2px;background:var(--line);}
.pulse-landing .tl-item .dt{width:24px;height:24px;border-radius:9px;flex:none;display:grid;place-items:center;background:rgba(14,124,255,.12);color:var(--blue);font-family:var(--mono);font-size:.6rem;z-index:1;}
.pulse-landing .tl-item .dt.ok{background:rgba(201,242,75,.3);color:#5C8000;}
.pulse-landing .tl-item .c b{font-size:.86rem;display:block;font-weight:600;}
.pulse-landing .tl-item .c span{font-size:.8rem;color:var(--ink-2);}
.pulse-landing .integrations{background:linear-gradient(180deg,rgba(255,239,230,.55),rgba(245,247,252,0));}
.pulse-landing .int-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(148px,1fr));gap:.7rem;margin-top:2.4rem;}
.pulse-landing .int{background:#fff;border:1px solid var(--line-2);border-radius:15px;padding:.9rem;display:flex;align-items:center;gap:.6rem;transition:transform .2s,border-color .2s,box-shadow .2s;}
.pulse-landing .int:hover{transform:translateY(-3px);border-color:rgba(14,124,255,.35);box-shadow:var(--shadow-s);}
.pulse-landing .int i{width:28px;height:28px;border-radius:9px;flex:none;display:grid;place-items:center;font-family:var(--display);font-weight:700;font-size:.78rem;color:#fff;font-style:normal;}
.pulse-landing .int span{font-size:.87rem;font-weight:500;}
.pulse-landing .impact{background:linear-gradient(150deg,#0E7CFF 0%,#0B2A6B 62%,#08205A 100%);color:#fff;border-radius:var(--r-xl);padding:clamp(34px,5vw,66px);position:relative;overflow:hidden;}
.pulse-landing .impact::before{content:'';position:absolute;inset:-40% -10% auto auto;width:560px;height:560px;background:radial-gradient(circle, rgba(201,242,75,.22), transparent 62%);pointer-events:none;}
.pulse-landing .impact::after{content:'';position:absolute;inset:auto auto -50% -12%;width:520px;height:520px;background:radial-gradient(circle, rgba(255,106,61,.26), transparent 62%);pointer-events:none;}
.pulse-landing .impact-in{position:relative;z-index:1;}
.pulse-landing .impact h2{max-width:16em;}
.pulse-landing .impact .lede{color:rgba(255,255,255,.76);margin-top:1rem;max-width:34em;}
.pulse-landing .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:clamp(16px,2vw,28px);margin-top:clamp(32px,4vw,52px);}
.pulse-landing .stat{border-top:1px solid rgba(255,255,255,.22);padding-top:1.1rem;}
.pulse-landing .stat .n{font-family:var(--display);font-weight:700;font-size:clamp(2rem,3.6vw,3rem);letter-spacing:-.045em;line-height:1;}
.pulse-landing .stat .n .u{color:var(--lime);}
.pulse-landing .stat .l{font-family:var(--mono);font-size:.7rem;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.6);margin-top:.6rem;}
.pulse-landing .tgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:clamp(14px,1.8vw,20px);}
.pulse-landing .t{background:#fff;border:1px solid var(--line-2);border-radius:var(--r-l);padding:clamp(20px,2.2vw,28px);display:flex;flex-direction:column;transition:transform .25s,box-shadow .25s;}
.pulse-landing .t:hover{transform:translateY(-4px);box-shadow:var(--shadow-m);}
.pulse-landing .t .metric{font-family:var(--display);font-weight:700;font-size:1.5rem;letter-spacing:-.035em;color:var(--blue);}
.pulse-landing .t.warm .metric{color:var(--coral);}
.pulse-landing .t q{display:block;font-size:.98rem;line-height:1.55;margin-top:.75rem;quotes:none;}
.pulse-landing .t .who{display:flex;align-items:center;gap:.65rem;margin-top:auto;padding-top:1.4rem;}
.pulse-landing .t .who i{width:36px;height:36px;border-radius:12px;flex:none;display:grid;place-items:center;font-family:var(--display);font-weight:700;font-size:.8rem;color:#fff;font-style:normal;}
.pulse-landing .t .who b{display:block;font-size:.87rem;}
.pulse-landing .t .who span{font-size:.78rem;color:var(--ink-3);}
.pulse-landing .price-head{text-align:center;max-width:44rem;margin:0 auto;}
.pulse-landing .toggle{display:inline-flex;background:rgba(15,26,51,.06);border-radius:999px;padding:4px;margin-top:1.8rem;}
.pulse-landing .toggle button{padding:.52em 1.1em;border-radius:999px;font-size:.87rem;font-weight:600;color:var(--ink-2);transition:.2s;}
.pulse-landing .toggle button.on{background:#fff;color:var(--ink);box-shadow:var(--shadow-s);}
.pulse-landing .toggle .save{font-family:var(--mono);font-size:.6rem;color:var(--coral);margin-left:.4em;letter-spacing:.06em;}
.pulse-landing .plans{display:grid;grid-template-columns:repeat(3,1fr);gap:clamp(14px,1.8vw,20px);margin-top:clamp(30px,3.6vw,46px);align-items:stretch;}
.pulse-landing .plan{background:#fff;border:1px solid var(--line-2);border-radius:var(--r-l);padding:clamp(24px,2.6vw,32px);display:flex;flex-direction:column;}
.pulse-landing .plan.feat-plan{background:linear-gradient(168deg,#0E7CFF,#0B2A6B);color:#fff;box-shadow:var(--shadow-l);border-color:transparent;position:relative;overflow:hidden;}
.pulse-landing .plan.feat-plan::after{content:'';position:absolute;inset:auto -20% -45% -20%;height:270px;background:radial-gradient(50% 60% at 50% 100%,rgba(201,242,75,.24),transparent 70%);pointer-events:none;}
.pulse-landing .plan .pn{font-family:var(--display);font-weight:700;font-size:1.2rem;letter-spacing:-.02em;}
.pulse-landing .plan .pd{font-size:.88rem;color:var(--ink-2);margin-top:.35rem;min-height:2.7em;}
.pulse-landing .plan.feat-plan .pd{color:rgba(255,255,255,.75);}
.pulse-landing .plan .amt{font-family:var(--display);font-weight:700;font-size:clamp(2.3rem,3.6vw,3rem);letter-spacing:-.05em;margin-top:1.2rem;line-height:1;}
.pulse-landing .plan .per{font-family:var(--mono);font-size:.7rem;color:var(--ink-3);letter-spacing:.06em;margin-top:.5rem;}
.pulse-landing .plan.feat-plan .per{color:rgba(255,255,255,.6);}
.pulse-landing .plan .btn{margin-top:1.4rem;width:100%;}
.pulse-landing .plan ul{list-style:none;margin:1.5rem 0 0;padding:0;position:relative;z-index:1;}
.pulse-landing .plan li{display:flex;gap:.6rem;font-size:.9rem;padding:.42rem 0;color:var(--ink-2);}
.pulse-landing .plan.feat-plan li{color:rgba(255,255,255,.85);}
.pulse-landing .plan li svg{width:15px;height:15px;flex:none;margin-top:.28rem;color:var(--blue);}
.pulse-landing .plan.feat-plan li svg{color:var(--lime);}
.pulse-landing .badge-pop{position:absolute;top:20px;right:20px;font-family:var(--mono);font-size:.6rem;letter-spacing:.12em;text-transform:uppercase;background:var(--lime);color:#33450A;padding:.32em .7em;border-radius:999px;z-index:2;}
.pulse-landing .faq{max-width:52rem;margin:0 auto;}
.pulse-landing .faq details{border-bottom:1px solid var(--line);}
.pulse-landing .faq summary{list-style:none;cursor:pointer;padding:1.25rem 2.6rem 1.25rem 0;position:relative;font-family:var(--display);font-weight:600;font-size:1.06rem;letter-spacing:-.02em;}
.pulse-landing .faq summary::-webkit-details-marker{display:none;}
.pulse-landing .faq summary::after{content:'';position:absolute;right:6px;top:1.55rem;width:11px;height:11px;border-right:2px solid var(--ink-3);border-bottom:2px solid var(--ink-3);transform:rotate(45deg);transition:transform .22s;}
.pulse-landing .faq details[open] summary::after{transform:rotate(-135deg);}
.pulse-landing .faq details p{padding:0 2.6rem 1.4rem 0;color:var(--ink-2);font-size:.96rem;}
.pulse-landing .finale{background:linear-gradient(155deg,#FFEFE6 0%,#FFE2D2 44%,#FFD9C4 100%);border-radius:var(--r-xl);padding:clamp(36px,5.5vw,78px);position:relative;overflow:hidden;text-align:center;}
.pulse-landing .finale::before{content:'';position:absolute;inset:-45% -20% auto -20%;height:640px;background:radial-gradient(45% 55% at 50% 50%, rgba(14,124,255,.22), transparent 70%);pointer-events:none;}
.pulse-landing .finale-in{position:relative;z-index:1;max-width:40rem;margin:0 auto;}
.pulse-landing .finale .lede{margin-top:1.1rem;color:#7A4E38;}
.pulse-landing .finale .btns{display:flex;gap:.75rem;justify-content:center;margin-top:2rem;flex-wrap:wrap;}
.pulse-landing .finale .fine{font-family:var(--mono);font-size:.72rem;color:#95614A;margin-top:1.4rem;letter-spacing:.03em;}
.pulse-landing .foot{background:var(--ink);color:#fff;padding-block:clamp(52px,6vw,80px) 2rem;margin-top:clamp(60px,7vw,100px);}
.pulse-landing .foot-top{display:grid;grid-template-columns:1.4fr repeat(4,1fr);gap:clamp(22px,3vw,40px);}
.pulse-landing .foot .logo{color:#fff;}
.pulse-landing .foot-about{font-size:.9rem;color:rgba(255,255,255,.6);margin-top:1.1rem;max-width:26em;line-height:1.6;}
.pulse-landing .socials{display:flex;gap:.5rem;margin-top:1.4rem;}
.pulse-landing .socials a{width:34px;height:34px;border-radius:11px;background:rgba(255,255,255,.08);display:grid;place-items:center;transition:background .2s;}
.pulse-landing .socials a:hover{background:var(--blue);}
.pulse-landing .socials svg{width:15px;height:15px;}
.pulse-landing .fcol h5{font-family:var(--mono);font-size:.66rem;letter-spacing:.14em;text-transform:uppercase;color:rgba(255,255,255,.45);margin:0 0 1rem;font-weight:400;}
.pulse-landing .fcol a{display:block;font-size:.9rem;color:rgba(255,255,255,.72);padding:.32rem 0;transition:color .18s;}
.pulse-landing .fcol a:hover{color:#fff;}
.pulse-landing .foot-bot{margin-top:clamp(38px,5vw,60px);padding-top:1.6rem;border-top:1px solid rgba(255,255,255,.12);display:flex;justify-content:space-between;gap:1rem;flex-wrap:wrap;font-family:var(--mono);font-size:.72rem;color:rgba(255,255,255,.45);}
.pulse-landing .foot-bot .badges{display:flex;gap:1.2rem;flex-wrap:wrap;}
.pulse-landing [data-reveal]{opacity:0;transform:translateY(22px);transition:opacity .7s cubic-bezier(.2,.7,.3,1),transform .7s cubic-bezier(.2,.7,.3,1);}
.pulse-landing [data-reveal].in{opacity:1;transform:none;}
@media (max-width:1020px){
  .pulse-landing .hero-in{grid-template-columns:1fr;}
  .pulse-landing .feat{grid-template-columns:1fr;}
  .pulse-landing .feat.rev .feat-copy{order:0;}
  .pulse-landing .tgrid{grid-template-columns:repeat(2,1fr);}
  .pulse-landing .stats{grid-template-columns:repeat(2,1fr);}
  .pulse-landing .plans{grid-template-columns:1fr;}
  .pulse-landing .foot-top{grid-template-columns:1fr 1fr 1fr;}
}
@media (max-width:860px){
  .pulse-landing .nav-links,.pulse-landing .nav-actions .signin,.pulse-landing .nav-actions .btn-ghost{display:none;}
  .pulse-landing .burger{display:grid;}
  .pulse-landing .cols{grid-template-columns:1fr;}
  .pulse-landing .bento{grid-template-columns:repeat(2,1fr);}
  .pulse-landing .b-1,.pulse-landing .b-2,.pulse-landing .b-3,.pulse-landing .b-4,.pulse-landing .b-5{grid-column:span 2;}
}
@media (max-width:640px){
  .pulse-landing{font-size:16px;}
  .pulse-landing .tgrid{grid-template-columns:1fr;}
  .pulse-landing .stats{grid-template-columns:1fr 1fr;}
  .pulse-landing .bento{grid-template-columns:1fr;}
  .pulse-landing .b-1,.pulse-landing .b-2,.pulse-landing .b-3,.pulse-landing .b-4,.pulse-landing .b-5{grid-column:span 1;}
  .pulse-landing .console-metrics{grid-template-columns:1fr 1fr;}
  .pulse-landing .cm:nth-child(3){display:none;}
  .pulse-landing .foot-top{grid-template-columns:1fr 1fr;}
  .pulse-landing .qi .state{min-width:80px;font-size:.6rem;}
}
@media (prefers-reduced-motion:reduce){
  .pulse-landing *{animation-duration:.001ms !important;animation-iteration-count:1 !important;transition-duration:.001ms !important;}
  .pulse-landing [data-reveal]{opacity:1;transform:none;}
}
`;

const LOGO = (
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12h4l2.5-7 4 14 3-9 2 2h4" />
  </svg>
);
const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const CROSS = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
/** Waits for the partner config so the page never flashes another brand's name
 *  or prices, then mounts the page once — its effects wire up the markup on
 *  mount, so it must be the final markup. */
export default function LandingPage() {
  const { config, loaded, fetchConfig } = usePartnerStore();

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  if (!loaded) return null;
  return <Landing partner={config} />;
}

const TAB_TITLE = `${APP_NAME} – AI-Powered Customer Communications for Small Businesses`;

function Landing({ partner }: { partner: PartnerConfig | null }) {
  const name = APP_NAME;
  const preset = partner?.theme;
  // Under Clerk's look the wordmark ends in Clerk's orange full stop: "Pulse.", as "Clerk.".
  const dot = preset === 'ledger' ? <span className="logo-dot">.</span> : null;
  // Faxter's own instance, or a partner's: the "by Faxter" credits are ours only.
  const ours = !partner || partner.slug === DEFAULT_PARTNER_SLUG;
  const market = marketFor(partner?.default_country);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set here too: the legal pages retitle the tab and this is a single-page app.
    document.title = TAB_TITLE;
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cleanups: Array<() => void> = [];
    const $ = <T extends Element>(sel: string) => root.querySelector(sel) as T | null;

    // Sticky nav
    const nav = $<HTMLElement>('#pl-nav');
    const onScroll = () => nav && nav.classList.toggle('stuck', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', onScroll));

    // Mobile menu
    const burger = $<HTMLButtonElement>('#pl-burger');
    const mm = $<HTMLElement>('#pl-mobileMenu');
    const onBurger = () => {
      const open = mm?.classList.toggle('open');
      burger?.setAttribute('aria-expanded', String(!!open));
    };
    burger?.addEventListener('click', onBurger);
    cleanups.push(() => burger?.removeEventListener('click', onBurger));

    // Scroll reveal
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => en.isIntersecting && (en.target.classList.add('in'), io.unobserve(en.target))),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    root.querySelectorAll('[data-reveal]').forEach((el, i) => {
      (el as HTMLElement).style.transitionDelay = Math.min(i, 4) * 60 + 'ms';
      io.observe(el);
    });
    cleanups.push(() => io.disconnect());

    // Animated stats
    const sio = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const el = en.target as HTMLElement;
          const target = +(el.dataset.count || '0');
          const pre = el.dataset.prefix || '';
          const suf = el.dataset.suffix || '';
          if (reduce) {
            el.innerHTML = pre + target + '<span class="u">' + suf + '</span>';
            sio.unobserve(el);
            return;
          }
          const start = performance.now();
          const step = (now: number) => {
            const p = Math.min((now - start) / 1400, 1);
            const v = Math.round(target * (1 - Math.pow(1 - p, 3)));
            el.innerHTML = pre + v + '<span class="u">' + suf + '</span>';
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          sio.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );
    root.querySelectorAll('[data-count]').forEach((el) => sio.observe(el));
    cleanups.push(() => sio.disconnect());

    // Pricing toggle
    const bM = $<HTMLButtonElement>('#pl-btnM');
    const bY = $<HTMLButtonElement>('#pl-btnY');
    const setPeriod = (annual: boolean) => {
      bM?.classList.toggle('on', !annual);
      bY?.classList.toggle('on', annual);
      root.querySelectorAll<HTMLElement>('.amt span[data-m]').forEach((s) => {
        s.textContent = '$' + (annual ? s.dataset.y : s.dataset.m);
      });
      root.querySelectorAll<HTMLElement>('.plan .per').forEach((p) => {
        if (p.textContent && p.textContent.indexOf('agent') > -1)
          p.textContent = annual ? 'per agent / month, billed yearly' : 'per agent / month';
      });
    };
    const onM = () => setPeriod(false);
    const onY = () => setPeriod(true);
    bM?.addEventListener('click', onM);
    bY?.addEventListener('click', onY);
    cleanups.push(() => {
      bM?.removeEventListener('click', onM);
      bY?.removeEventListener('click', onY);
    });

    // Live queue simulation
    const pool = [
      { n: 'Amara Ovie', c: '#0E7CFF', t: 'Billing', k: 'bill', m: 'Charged twice for the March invoice — can you check?' },
      { n: 'Ben Iversen', c: '#FF6A3D', t: 'How-to', k: '', m: 'How do I add a second workspace to my plan?' },
      { n: 'Chen Wei', c: '#8A6EFF', t: 'Bug', k: 'tech', m: 'CSV export returns an empty file on Safari.' },
      { n: 'Dara Okafor', c: '#3E8E24', t: 'Account', k: '', m: 'Need to move the seat licence to a new teammate.' },
      { n: 'Elif Kaya', c: '#0B2A6B', t: 'Billing', k: 'bill', m: 'Can we switch from monthly to annual mid-term?' },
      { n: 'Farid Nasser', c: '#C3421A', t: 'Bug', k: 'tech', m: 'Webhooks stopped firing after yesterday’s deploy.' },
      { n: 'Greta Lind', c: '#0E7CFF', t: 'How-to', k: '', m: 'Where do I find the audit log for last quarter?' },
      { n: 'Hana Sato', c: '#8A6EFF', t: 'Account', k: '', m: 'Please confirm our data is stored in the EU region.' },
    ];
    const queue = $<HTMLUListElement>('#pl-queue');
    const totEl = $<HTMLElement>('#pl-tot');
    let idx = 0;
    let resolved = 1284;
    const initials = (name: string) => name.split(' ').map((w) => w[0]).join('').slice(0, 2);
    const makeItem = (d: (typeof pool)[number]) => {
      const li = document.createElement('li');
      li.className = 'qi enter';
      li.innerHTML =
        '<span class="av" style="background:' + themed(d.c, preset) + '">' + initials(d.n) + '</span>' +
        '<span class="meta"><span class="who">' + d.n + ' <span class="tag ' + d.k + '">' + d.t + '</span></span>' +
        '<span class="msg">' + d.m + '</span></span>' +
        '<span class="state">queued</span>';
      return li;
    };
    if (queue) {
      for (let i = 0; i < 4; i++) {
        const li = makeItem(pool[idx % pool.length]);
        idx++;
        li.classList.remove('enter');
        queue.appendChild(li);
      }
    }
    let cycleTimer: number | undefined;
    let interval: number | undefined;
    const cycle = () => {
      const first = queue?.firstElementChild as HTMLElement | null;
      if (!first) return;
      const st = first.querySelector('.state') as HTMLElement;
      st.className = 'state drafting';
      st.textContent = `${name} drafting`;
      st.insertAdjacentHTML('beforeend', '<span class="bar"><i></i></span>');
      window.setTimeout(() => {
        st.className = 'state resolved';
        st.textContent = '✓ resolved · 1.4s';
        first.classList.add('is-resolved');
        resolved++;
        if (totEl) totEl.textContent = resolved.toLocaleString();
        window.setTimeout(() => {
          first.classList.add('leave');
          window.setTimeout(() => {
            first.remove();
            const li = makeItem(pool[idx % pool.length]);
            idx++;
            queue?.appendChild(li);
          }, 480);
        }, 850);
      }, 1250);
    };
    if (!reduce && queue) {
      cycleTimer = window.setTimeout(() => {
        cycle();
        interval = window.setInterval(cycle, 3600);
      }, 1200);
    }
    cleanups.push(() => {
      if (cycleTimer) clearTimeout(cycleTimer);
      if (interval) clearInterval(interval);
    });

    // Conviction counters
    const counters = [
      { el: $<HTMLElement>('#pl-cA'), v: 128450, step: [1, 4] },
      { el: $<HTMLElement>('#pl-cB'), v: 24918, step: [1, 2] },
      { el: $<HTMLElement>('#pl-cC'), v: 3206, step: [0, 1] },
    ];
    let ctr: number | undefined;
    if (!reduce) {
      let seed = 7;
      const rnd = () => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
      };
      ctr = window.setInterval(() => {
        counters.forEach((c) => {
          c.v += c.step[0] + Math.floor(rnd() * (c.step[1] - c.step[0] + 1));
          if (c.el) c.el.textContent = c.v.toLocaleString();
        });
      }, 2000);
    }
    cleanups.push(() => ctr && clearInterval(ctr));

    return () => cleanups.forEach((fn) => fn());
  }, [name, preset]);

  return (
    <div className="pulse-landing" ref={rootRef}>
      <style dangerouslySetInnerHTML={{ __html: themed(CSS, preset) }} />

      {/* Announcement */}
      <div className="topbar">
        <span className="dot" />
        <span>{name} answers WhatsApp &amp; web chat 24/7 — and escalates the rest to your team.</span>
        <a href="#features">See how it works</a>
      </div>

      {/* Nav */}
      <div className="nav-shell">
        <nav className="nav" id="pl-nav">
          <div className="wrap nav-in">
            <Link to="/" className="logo">
              <span className="logo-mark">{LOGO}</span>
              {name}
              {dot} {ours ? <small>by faxter</small> : null}
            </Link>
            <div className="nav-links">
              <a href="#features">Product</a>
              <a href="#workflow">Solutions</a>
              <a href="#integrations">Integrations</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">Resources</a>
            </div>
            <div className="nav-actions">
              <Link to="/login" className="signin">Sign in</Link>
              <Link to="/register" className="btn btn-ghost">Book a demo</Link>
              <Link to="/register" className="btn btn-primary">Start free</Link>
              <button className="burger" id="pl-burger" aria-label="Open menu" aria-expanded="false"><span /></button>
            </div>
          </div>
        </nav>
        <div className="mobile-menu" id="pl-mobileMenu">
          <div className="wrap" style={{ padding: 0 }}>
            <a href="#features">Product</a>
            <a href="#workflow">Solutions</a>
            <a href="#integrations">Integrations</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">Resources</a>
            <Link to="/register" className="btn btn-primary">Start free</Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="hero">
        <div className="hero-glow" />
        <div className="wrap hero-in">
          <div>
            <div className="rating">
              <span><b>WhatsApp-first</b> AI support, built for lean teams</span>
            </div>
            <h1>Every conversation read, triaged, and answered. <span className="accent">Automatically.</span></h1>
            <p className="lede">{name} is the AI support desk for WhatsApp-first teams. It answers routine questions the moment they land, triages every conversation by intent and urgency, and escalates the rest to your team — with the context already attached.</p>
            <div className="hero-cta">
              <Link to="/register" className="btn btn-accent btn-lg">Start free for 14 days</Link>
              <a href="#workflow" className="btn btn-ghost btn-lg">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                Watch the 2-min tour
              </a>
            </div>
            <div className="hero-trust">
              <span>{CHECK} No card details needed</span>
              <span>{CHECK} Live in under an hour</span>
              <span>{CHECK} Your data stays in your tenant</span>
            </div>
          </div>

          {/* Live console */}
          <div style={{ position: 'relative' }}>
            <div className="console" aria-hidden="true">
              <div className="console-bar">
                <span className="tl"><i /><i /><i /></span>
                <span className="title">{name.toLowerCase()} / inbox — whatsapp &amp; web</span>
                <span className="live-pill"><i /> Live</span>
              </div>
              <div className="console-metrics">
                <div className="cm"><div className="k">First response</div><div className="v">8<small>s</small> <span className="up">↓ 96%</span></div></div>
                <div className="cm"><div className="k">Auto-resolved</div><div className="v">71<small>%</small></div></div>
                <div className="cm"><div className="k">CSAT</div><div className="v">4.8<small>/5</small></div></div>
              </div>
              <ul className="queue" id="pl-queue" />
              <div className="console-foot">
                <div className="ecg">
                  <svg viewBox="0 0 700 40" preserveAspectRatio="none">
                    <path d="M0 20 H70 l9 -15 l9 30 l10 -15 H190 l12 -8 l9 16 l10 -8 H330 l9 -18 l9 36 l10 -18 H460 l12 -7 l9 14 l10 -7 H610 l9 -15 l9 30 l10 -15 H700" />
                  </svg>
                </div>
                <div className="tot">resolved today <b id="pl-tot">1,284</b></div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Social proof */}
      <section className="proof">
        <div className="wrap cap eyebrow">Works with the tools you already use</div>
        <div className="marquee">
          <div className="marquee-track">
            {['WhatsApp', 'Web Chat', 'Shopify', 'WooCommerce', 'HubSpot', 'Any REST API', 'WhatsApp', 'Web Chat', 'Shopify', 'WooCommerce', 'HubSpot', 'Any REST API'].map((n, i) => (
              <span key={i}>{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="section" id="workflow">
        <div className="wrap">
          <div className="split-head" data-reveal>
            <div className="eyebrow">The choice every support lead is making</div>
            <h2 style={{ marginTop: '1rem' }}>Your inbox is <span className="swap"><span className="old">a backlog</span></span> <span className="swap"><span className="new">a head start</span></span></h2>
            <p className="lede">Same volume, same team. The difference is whether the work arrives sorted or arrives raw.</p>
          </div>
          <div className="cols">
            <div className="col col-a" data-reveal>
              <div className="col-title"><span className="ic">{CROSS}</span>A traditional helpdesk</div>
              {[
                ['Monday morning is a wall', '480 unread, no order to them. The first hour goes to deciding what to read, not to answering anyone.'],
                ['Every answer written from scratch', 'The same shipping question, retyped 40 times a week, slightly differently each time.'],
                ['The angry ticket surfaces last', 'Churn risk sits in position 312 because the queue is sorted by arrival time, not by stakes.'],
                ['Nobody knows what broke', 'Twelve people reported the same bug. It reads as twelve tickets, never as one signal.'],
              ].map(([h, p], i) => (
                <div className="row" key={i}><h4><span className="ic">{CROSS}</span>{h}</h4><p>{p}</p></div>
              ))}
            </div>
            <div className="col col-b" data-reveal>
              <div className="col-title"><span className="ic">{CHECK}</span>Support running on {name}</div>
              {[
                ['Routine questions, already answered', 'Common asks — order status, hours, FAQs — get an instant reply from your knowledge base, day or night.'],
                ['Every conversation arrives triaged', "Intent, sentiment, and priority are set the moment a message lands, and it's assigned to the right teammate."],
                ['Urgency jumps the queue', `${name} reads sentiment on every message and bumps the frustrated, high-priority ones to the top automatically.`],
                ['Your team stays in flow', 'A private AI copilot drafts replies, looks up orders, and can edit Shopify carts — right inside the conversation.'],
              ].map(([h, p], i) => (
                <div className="row" key={i}><h4><span className="ic">{CHECK}</span>{h}</h4><p>{p}</p></div>
              ))}
              <div className="kicker">Flat per-seat pricing · AI included · No per-resolution fees</div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento */}
      <section className="section" id="features" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="split-head" data-reveal>
            <div className="eyebrow">What's in the box</div>
            <h2 style={{ marginTop: '1rem' }}>Built to make the next hour easier</h2>
            <p className="lede">Not a chatbot bolted onto an inbox. A support surface where answering, triage, escalation, and your team's copilot are one continuous motion.</p>
          </div>
          <div className="bento">
            <article className="card b-1" data-reveal>
              <div className="chip">Triage</div>
              <h3>Sorted before you open it</h3>
              <p>Every incoming message is read, tagged by intent, scored for urgency and sentiment, and assigned to the right teammate within seconds of arriving.</p>
              <div className="mini">
                <div className="mini-row"><span className="pill">Billing</span> Refund not received — high priority</div>
                <div className="mini-row"><span className="pill">Order</span> Where is my order? — auto-answered</div>
                <div className="mini-row"><span className="pill">How-to</span> Change my address — self-serve</div>
              </div>
            </article>
            <article className="card b-2 card-peach" data-reveal>
              <div className="chip">Answering</div>
              <h3>Grounded in your knowledge base</h3>
              <p>{name} answers from your own help articles and connected tools — order status, customer history — so replies are accurate, not generic support-bot English.</p>
              <div className="bars">
                {[34, 52, 41, 88, 63, 47, 76, 58, 38].map((h, i) => (
                  <i key={i} className={h > 70 ? 'hot' : ''} style={{ height: h + '%' }} />
                ))}
              </div>
            </article>
            <article className="card b-3" data-reveal>
              <div className="chip">Escalation</div>
              <h3>Humans get the hard half</h3>
              <p>When a question needs a person, {name} hands off to the right teammate with the reason, the priority, and the full conversation attached.</p>
            </article>
            <article className="card b-4" data-reveal>
              <div className="chip">Copilot</div>
              <h3>An AI teammate in every thread</h3>
              <p>A private copilot drafts replies, looks up orders, and can edit Shopify carts — right inside the conversation. Nothing reaches the customer until your staff posts it.</p>
            </article>
            <article className="card b-5" data-reveal>
              <div className="chip">Team &amp; insight</div>
              <h3>Team chat + live analytics</h3>
              <p>A store-wide staff channel with an @mention AI and scheduled duties, plus a dashboard for resolution rate, volume, per-agent load, and CSAT.</p>
              <div className="langs">{['Resolution', 'CSAT', 'Volume', 'Per-agent', 'Sentiment'].map((l) => <b key={l}>{l}</b>)}</div>
            </article>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="section integrations" id="integrations">
        <div className="wrap">
          <div className="split-head" data-reveal>
            <div className="eyebrow">Works where you already work</div>
            <h2 style={{ marginTop: '1rem' }}>Connect the tools your store already runs on.</h2>
            <p className="lede">{name} plugs into WhatsApp and your storefront out of the box — and into any REST API you can describe with an OpenAPI spec. No rip-and-replace.</p>
          </div>
          <div className="int-grid" data-reveal>
            {[
              ['#25D366', 'W', 'WhatsApp'], ['#0E7CFF', 'C', 'Web Chat'], ['#95BF47', 'S', 'Shopify'], ['#7F54B3', 'W', 'WooCommerce'],
              ['#FF7A59', 'H', 'HubSpot'], ['#0B2A6B', 'V', 'Voice (beta)'], ['#0E7CFF', '{}', 'Any REST API'], ['#FF6A3D', '+', 'Request one'],
            ].map(([bg, letter, name], i) => (
              <div className="int" key={i}><i style={{ background: bg }}>{letter}</i><span>{name}</span></div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="impact" data-reveal>
            <div className="impact-in">
              <div className="eyebrow" style={{ color: 'var(--lime)' }}>What {name} does out of the box</div>
              <h2 style={{ marginTop: '1rem' }}>The hours come back to the people who were losing them</h2>
              <p className="lede">No fabricated benchmarks — here's what {name} actually does the day you switch it on. Your own numbers show up in the analytics dashboard.</p>
              <div className="stats">
                <div className="stat"><div className="n" data-count="24" data-suffix="/7">0</div><div className="l">AI first response, any hour</div></div>
                <div className="stat"><div className="n" data-count="100" data-suffix="%">0</div><div className="l">Messages auto-triaged &amp; prioritised</div></div>
                <div className="stat"><div className="n" data-count="3">0</div><div className="l">Channels: WhatsApp, web, voice (beta)</div></div>
                <div className="stat"><div className="n" data-count="0">0</div><div className="l">Per-resolution fees, ever</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="split-head" data-reveal>
            <div className="eyebrow">How it works</div>
            <h2 style={{ marginTop: '1rem' }}>Live in three steps</h2>
          </div>
          <div className="tgrid">
            {[
              ['warm', '1 · Connect', 'Link WhatsApp with embedded signup, or drop the web-chat widget on your site. A few minutes, no code.'],
              ['', '2 · Teach', `Add your help articles, hours, and policies. Connect Shopify, WooCommerce, or HubSpot so ${name} can look up real orders and customers.`],
              ['warm', '3 · Go live', `${name} answers the routine questions, triages and assigns the rest, and your team takes over with an AI copilot in every thread.`],
            ].map(([warm, metric, body], i) => (
              <article className={'t ' + warm} data-reveal key={i}>
                <div className="metric">{metric}</div>
                <q>{body}</q>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section" id="pricing" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="price-head" data-reveal>
            <div className="eyebrow">Pricing</div>
            <h2 style={{ marginTop: '1rem' }}>Priced per person, not per problem</h2>
            <p className="lede" style={{ marginTop: '1rem' }}>AI is included in every plan. You'll never be charged more for a busy week — which is exactly when you need the help most.</p>
            {market.publishedPrices ? (
              <div className="toggle" role="group" aria-label="Billing period">
                <button id="pl-btnM" className="on">Monthly</button>
                <button id="pl-btnY">Annual <span className="save">−20%</span></button>
              </div>
            ) : null}
          </div>
          <div className="plans">
            <article className="plan" data-reveal>
              <div className="pn">Starter</div>
              <p className="pd">For a small team handling everything in one shared inbox.</p>
              {market.publishedPrices ? (
                <>
                  <div className="amt"><span data-m="19" data-y="15">$19</span></div>
                  <div className="per">per agent / month</div>
                </>
              ) : (
                <div className="amt">On request</div>
              )}
              <Link to="/register" className="btn btn-ghost">Start free</Link>
              <ul>{['Up to 3 agents', 'WhatsApp + web chat', 'AI answers from your knowledge base', 'Auto-triage, sentiment & priority', 'CSAT surveys'].map((f) => <li key={f}>{CHECK}{f}</li>)}</ul>
            </article>
            <article className="plan feat-plan" data-reveal>
              <span className="badge-pop">Most teams start here</span>
              <div className="pn">Team</div>
              <p className="pd">For support teams who want {name} answering, not just suggesting.</p>
              {market.publishedPrices ? (
                <>
                  <div className="amt"><span data-m="39" data-y="31">$39</span></div>
                  <div className="per">per agent / month</div>
                </>
              ) : (
                <div className="amt">On request</div>
              )}
              <Link to="/register" className="btn btn-accent">Start free for 14 days</Link>
              <ul>{['Everything in Starter', 'AI Copilot in every conversation', 'Team chat + scheduled AI duties', 'Shopify, WooCommerce & HubSpot', 'Voice agent (beta)', 'Analytics dashboard'].map((f) => <li key={f}>{CHECK}{f}</li>)}</ul>
            </article>
            <article className="plan" data-reveal>
              <div className="pn">Scale</div>
              <p className="pd">For larger stores and teams that need custom connections and hands-on setup.</p>
              <div className="amt">Let's talk</div>
              <div className="per">annual agreement</div>
              <Link to="/register" className="btn btn-primary">Book a demo</Link>
              <ul>{['Everything in Team', 'Connect any REST API (OpenAPI)', 'Custom onboarding & knowledge setup', 'Priority support', 'Volume pricing'].map((f) => <li key={f}>{CHECK}{f}</li>)}</ul>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" id="faq" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="split-head" data-reveal>
            <div className="eyebrow">Questions we get asked</div>
            <h2 style={{ marginTop: '1rem' }}>The honest answers</h2>
          </div>
          <div className="faq" data-reveal>
            {[
              [`Does ${name} reply to customers on its own?`, 'Yes — for routine questions (order status, hours, FAQs) it answers directly on WhatsApp and web chat, grounded in your knowledge base. When a question needs a person, it escalates to your team instead of guessing. A pre-send approval mode, where a person approves every draft, is on our roadmap.', true],
              ["What happens when it doesn't know the answer?", "It escalates to the right teammate rather than guessing — with the reason, the priority, and the full conversation attached. It only answers from your knowledge base and connected tools, so it won't invent a policy you didn't give it.", false],
              ['How long does it take to get running?', "Connect your WhatsApp number with embedded signup (or drop the web-chat widget on your site), add your knowledge base, and you're live. Most stores are up in under an hour.", false],
              ['Do we get charged more when volume spikes?', "No. Pricing is per agent per month, flat. A bad week costs the same as a quiet one — that's deliberate, because usage-based AI pricing punishes you precisely when the tool is doing the most for you.", false],
              ['Where does our customer data go?', "Each store's data is isolated in its own tenant. We call LLM providers over their APIs and don't use your conversations to train shared models. Formal SOC 2 / GDPR tooling (data export and erasure) is on our roadmap — ask us where it stands.", false],
              [`What can ${name} connect to?`, `Out of the box: WhatsApp, a web-chat widget, Shopify, WooCommerce, and HubSpot. Beyond that, you can connect any REST API by uploading its OpenAPI spec and choosing which endpoints ${name} may call — so it looks up orders and customer data where they live.`, false],
            ].map(([q, a, open], i) => (
              <details key={i} open={open as boolean}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="finale" data-reveal>
            <div className="finale-in">
              <div className="eyebrow" style={{ color: '#B0553A' }}>Ready when you are</div>
              <h2 style={{ marginTop: '1rem' }}>Give your team a queue that's already half-answered</h2>
              <p className="lede">Connect WhatsApp or your web chat, add your knowledge base, and watch {name} answer for a day. Fourteen days, everything included, nothing to uninstall if you walk away.</p>
              <div className="btns">
                <Link to="/register" className="btn btn-accent btn-lg">Start free for 14 days</Link>
                <Link to="/register" className="btn btn-ghost btn-lg">Talk to our team</Link>
              </div>
              <div className="fine">No card details needed · Live in under an hour · Cancel any time</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="foot">
        <div className="wrap">
          <div className="foot-top">
            <div>
              <Link to="/" className="logo"><span className="logo-mark">{LOGO}</span>{name}{dot}</Link>
              <p className="foot-about">{name} is the AI support desk that answers your WhatsApp and web-chat conversations, triages every one, and escalates what needs a person to your team.{ours ? ' Built by Faxter.' : ''}</p>
            </div>
            <div className="fcol">
              <h5>Product</h5>
              <a href="#features">Triage</a><a href="#features">Answering</a><a href="#features">Copilot</a><a href="#features">Escalation</a><a href="#pricing">Pricing</a>
            </div>
            <div className="fcol">
              <h5>Solutions</h5>
              <a href="#workflow">SaaS teams</a><a href="#workflow">E-commerce</a><a href="#workflow">Fintech</a><a href="#workflow">Marketplaces</a><a href="#workflow">Healthcare</a>
            </div>
            <div className="fcol">
              <h5>Resources</h5>
              <a href="#faq">Help centre</a><a href="#integrations">API docs</a><a href="#features">Customer stories</a><a href="#features">Blog</a><a href="#features">System status</a>
            </div>
            <div className="fcol">
              <h5>Company</h5>
              {ours ? <a href="#">About Faxter</a> : null}<a href="#">Careers</a><a href="#">Security</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a>
            </div>
          </div>
          <div className="foot-bot">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {!ours && partner?.logo_url ? (
                <img
                  src={partner.logo_url}
                  alt={partner.name}
                  style={{ height: 24, width: 'auto', borderRadius: 4 }}
                />
              ) : null}
              © 2026 {ours ? `${name} by Faxter` : partner?.name}. All rights reserved.
            </div>
            <div className="badges"><span>WhatsApp Business API</span><span>Your data, your tenant</span>{ours ? <span>Built by Faxter</span> : null}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
